import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ROUTES from '../routes/routeConstants';
import {
  ScenarioControllerApi,
  YearPlanControllerApi,
  PatientControllerApi
} from '../api/generated';
import type { YearPlanResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setPatients, selectPatientsById } from '../store/patients/patientsSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { getPlanLockedMessage } from '../services/scenarioLockErrorService';
import { getApiErrorMessage } from '../utils/errorMessageUtils';
import { HTTP_CONFLICT } from '../constants/httpConstants';
import type { EconomicsLeafState, EconomicsTreeModel } from '../types/economicsTypes';
import {
  applyCompletion,
  buildClaimStates,
  buildLeafStates,
  buildTree,
  claimFullyCompleted,
  diffChanges,
  diffClaims
} from '../services/economicsTree';
import { saveEconomicsBatch } from '../services/economicsService';
import { getDefaultPeriod } from '../services/economicsPeriodService';
import { useLocationName } from './useLocationName';

function cloneLeaves(leaves: Map<string, EconomicsLeafState>): Map<string, EconomicsLeafState> {
  return new Map([...leaves].map(([key, state]) => [key, { ...state }]));
}

export function useEconomics(locationId: string | undefined) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const patientsById = useAppSelector(selectPatientsById);

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const scenarioYearRef = useRef<number>(new Date().getFullYear());
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Read from the shared locations slice rather than fetched here — the header
  // switcher already has the list. Same change as the year overview's, for the
  // same reason: this page fetched the whole list on every load just to read
  // one name. A list that cannot be loaded still sends the page to the error
  // page — see useLocationName.
  const locationName = useLocationName(locationId);

  const [yearPlan, setYearPlan] = useState<YearPlanResponse | null>(null);
  const [working, setWorking] = useState<Map<string, EconomicsLeafState>>(new Map());
  const [workingClaims, setWorkingClaims] = useState<Map<string, boolean>>(new Map());
  const [fromWeek, setFromWeek] = useState(0);
  const [toWeek, setToWeek] = useState(0);

  // Server snapshots, derived from the loaded year plan — the baselines the working copies diff against.
  const serverLeaves = useMemo(
    () => (yearPlan ? buildLeafStates(yearPlan) : new Map<string, EconomicsLeafState>()),
    [yearPlan]
  );
  const serverClaims = useMemo(
    () => (yearPlan ? buildClaimStates(yearPlan) : new Map<string, boolean>()),
    [yearPlan]
  );

  // Applies a fresh server year plan: rebuilds the working copies, optionally resetting the selected
  // period to the default one (on first load / year change, not on a post-save re-fetch).
  const applyServerYearPlan = useCallback((plan: YearPlanResponse, resetPeriod: boolean) => {
    setYearPlan(plan);
    setWorking(buildLeafStates(plan));
    setWorkingClaims(buildClaimStates(plan));
    if (resetPeriod) {
      const period = getDefaultPeriod(plan.weeks);
      setFromWeek(period.from);
      setToWeek(period.to);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!locationId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);
        const patientApi = new PatientControllerApi(config);

        const scenarioResponse = await scenarioApi.getCurrentScenario({ locationId });

        const fetchedScenarioId = scenarioResponse.data.id;
        setScenarioId(fetchedScenarioId ?? null);
        scenarioYearRef.current = scenarioResponse.data.year;
        setSelectedYear(scenarioResponse.data.year);

        if (fetchedScenarioId) {
          const [yearPlanResponse, patientsResponse] = await Promise.all([
            yearPlanApi.getYearPlan({ locationId, scenarioId: fetchedScenarioId }),
            patientApi.listPatients({ locationId })
          ]);
          applyServerYearPlan(yearPlanResponse.data, true);
          dispatch(setPatients(patientsResponse.data));
        }
      } catch (error) {
        console.error('Failed to load economics on initial fetch:', error);
        navigate(`/${ROUTES.errorPage}`);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [locationId, applyServerYearPlan, dispatch, navigate]);

  // Year change
  useEffect(() => {
    if (!locationId || selectedYear === scenarioYearRef.current) return;
    const fetchYearPlan = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);
        const patientApi = new PatientControllerApi(config);

        const scenarioResponse = await scenarioApi.getScenarioForYear({ locationId, year: selectedYear });
        const fetchedScenarioId = scenarioResponse.data.id;
        setScenarioId(fetchedScenarioId ?? null);
        scenarioYearRef.current = scenarioResponse.data.year;

        if (fetchedScenarioId) {
          const [yearPlanResponse, patientsResponse] = await Promise.all([
            yearPlanApi.getYearPlan({ locationId, scenarioId: fetchedScenarioId }),
            patientApi.listPatients({ locationId })
          ]);
          applyServerYearPlan(yearPlanResponse.data, true);
          dispatch(setPatients(patientsResponse.data));
        } else {
          setYearPlan(null);
          setWorking(new Map());
          setWorkingClaims(new Map());
        }
      } catch (error) {
        console.error('Failed to load economics on year change:', error);
        dispatch(showError({ message: t('fetch-error') }));
        setSelectedYear(scenarioYearRef.current);
      } finally {
        setLoading(false);
      }
    };
    void fetchYearPlan();
  }, [locationId, selectedYear, applyServerYearPlan, dispatch, t]);

  const getPatientName = useCallback(
    (patientId: string) => patientsById[patientId]?.name ?? t('economics-unknown-patient'),
    [patientsById, t]
  );

  const tree: EconomicsTreeModel = useMemo(() => {
    if (!yearPlan) return { plans: [], grandTotal: 0, completedCount: 0 };
    return buildTree(yearPlan, working, workingClaims, serverClaims, fromWeek, toWeek, getPatientName);
  }, [yearPlan, working, workingClaims, serverClaims, fromWeek, toWeek, getPatientName]);

  const changes = useMemo(() => diffChanges(serverLeaves, working), [serverLeaves, working]);
  const claimChanges = useMemo(() => diffClaims(serverClaims, workingClaims), [serverClaims, workingClaims]);
  const isDirty = changes.length > 0 || claimChanges.length > 0;

  const setPeriod = useCallback((from: number, to: number) => {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    setFromWeek(lo);
    setToWeek(hi);
  }, []);

  // The patient and plan shortcuts. Completing through them also claims the completion
  // reimbursement of every patient they leave with all groups on the plan completed;
  // uncompleting leaves existing claims alone.
  const toggleKeys = useCallback((keys: string[], completed: boolean) => {
    const next = applyCompletion(working, keys, completed);
    setWorking(next);
    if (completed) setWorkingClaims((prev) => claimFullyCompleted(prev, next, keys));
  }, [working]);

  // A single group. Never claims, even when it completes the patient's last group.
  const toggleLeaf = useCallback((key: string, completed: boolean) => {
    setWorking((prev) => applyCompletion(prev, [key], completed));
  }, []);

  // The note is mandatory whenever `value` differs from what the server has — the dialog enforces
  // that before calling this, and the backend rejects the batch if it somehow gets through.
  const setOverride = useCallback((key: string, value: number | null, note: string | null) => {
    setWorking((prev) => {
      const current = prev.get(key);
      if (!current) return prev;
      const next = new Map(prev);
      next.set(key, { completed: current.completed, reimbursementOverride: value, note });
      return next;
    });
  }, []);

  // Flips one completion claim by hand. The row only allows claiming once every group on the
  // plan is completed (see `isClaimable`); un-claiming is always allowed.
  const toggleClaim = useCallback((key: string, claimed: boolean) => {
    setWorkingClaims((prev) => {
      const next = new Map(prev);
      next.set(key, claimed);
      return next;
    });
  }, []);

  const discard = useCallback(() => {
    setWorking(cloneLeaves(serverLeaves));
    setWorkingClaims(new Map(serverClaims));
  }, [serverLeaves, serverClaims]);

  const save = useCallback(async () => {
    if (!locationId || !scenarioId || (changes.length === 0 && claimChanges.length === 0)) return;
    setSaving(true);
    try {
      await saveEconomicsBatch(locationId, scenarioId, changes, claimChanges);
      const config = createApiConfiguration();
      const yearPlanApi = new YearPlanControllerApi(config);
      const refreshed = await yearPlanApi.getYearPlan({ locationId, scenarioId });
      applyServerYearPlan(refreshed.data, false);
    } catch (error) {
      console.error('Failed to save economics changes', error);
      // The batch endpoint is lock-guarded, so a 409 here is two different failures wearing the
      // same status: another user holding the plan lock, and a genuine save conflict. The lock
      // check has to come first, or a lock clash keeps reading as "refresh and try again" — advice
      // that never resolves it.
      const lockMessage = getPlanLockedMessage(error, t);
      if (lockMessage) {
        dispatch(showError({ message: lockMessage }));
        return;
      }
      const isConflict = axios.isAxiosError(error) && error.response?.status === HTTP_CONFLICT;
      // A stale-write conflict has no message worth showing, so it keeps its fixed advice.
      // Anything else defers to a message the server sent, as the other save paths do.
      dispatch(showError({
        message: isConflict
          ? t('economics-save-conflict')
          : getApiErrorMessage(error, t('economics-save-error'))
      }));
    } finally {
      setSaving(false);
    }
  }, [locationId, scenarioId, changes, claimChanges, applyServerYearPlan, dispatch, t]);

  return {
    yearPlan,
    selectedYear,
    setSelectedYear,
    scenarioId,
    locationName,
    loading,
    saving,
    weeks: yearPlan?.weeks ?? [],
    fromWeek,
    toWeek,
    setPeriod,
    tree,
    isDirty,
    toggleLeaf,
    toggleKeys,
    toggleClaim,
    setOverride,
    save,
    discard
  };
}
